import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Loader from "../../components/Loader";
import AddProductsHeader from "../../components/Products/AddProductsHeader";
import CurrentProducts from "./CurrentProducts";
import Pagination from "./Pagination";
import RenderPaginationNumbers from "./RenderPageNumber";
import SearchAndAddProductBar from "./SearchAndAddProductBar";
import DeleteModel from "../../components/Products/DeleteModel";
import { toast, ToastContainer } from "react-toastify";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../contexts/AuthProvider";

import { API_URL } from "../../utils/config";
const baseUrl = API_URL;

function ProductsList() {
  const { t } = useTranslation("productlist");
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  const [searchValue, setSearchValue] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  const [isDel, setIsDel] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const [isTogglingStatus, setIsTogglingStatus] = useState(false);
  const { isAuthenticated } = useAuth();
  const itemsPerPage = 10;
  const navigate = useNavigate();

  const getLangFromLocalStorage = () => {
    return localStorage.getItem("language") || "en";
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleSearchChange = useCallback((searchTerm) => {
    setSearchValue(searchTerm);
    setCurrentPage(1); // Reset to first page when search changes
  }, []);

  // Debounced search effect
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      // Only trigger API call if we're not currently deleting
      if (!isDeleting) {
        fetchProducts();
      }
    }, searchValue ? 1000 : 0); // Debounce search by 1000ms, but immediate for non-search

    return () => clearTimeout(timeoutId);
  }, [searchValue, currentPage, isDeleting]);

  const fetchProducts = async () => {
    if (!isAuthenticated) {
      localStorage.removeItem("user");
      window.location.href = "/";
      return;
    }
    
    setLoading(true);
    
    try {
      const params = new URLSearchParams({
        limit: itemsPerPage.toString(),
        page: currentPage.toString(),
        ...(searchValue && { search: searchValue })
      });
      
      const url = `${baseUrl}/api/v1/attnd-supplier/services?${params}`;
      const response = await fetch(url, {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          lang: getLangFromLocalStorage(),
        },
      });

      if (response.status === 401) {
        navigate("/login");
        throw new Error("Unauthorized access. Please log in again.");
      }

      if (!response.ok) {
        throw new Error("Network response was not ok");
      }

      const responseData = await response.json();
      setProducts(responseData.data);
      setTotalPages(responseData.totalPages);
      setTotalItems(responseData.totalItems);
    } catch (error) {
      console.error("Error fetching products:", error);
      setProducts([]);
      setTotalPages(1);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (isDeleting) return;
    setIsDeleting(true);
    try {
      if (!isAuthenticated) {
        localStorage.removeItem("user");
        window.location.href = "/";
        return;
      }
      const response = await fetch(`${baseUrl}/api/v1/attnd-supplier/services/${isDel}`, {
        method: "DELETE",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          lang: getLangFromLocalStorage(),
        },
      });

      if (!response.ok) {
        if (response.status === 401) {
          navigate("/login");
          throw new Error("Unauthorized access. Please log in again.");
        }
        const errorData = await response.json();
        throw new Error(errorData.error);
      }
      // Remove deleted product from local state and update pagination
      setProducts((prev) => prev.filter((p) => p.id !== isDel));
      setTotalItems((prev) => prev - 1);
      
      // If current page becomes empty and we're not on page 1, go to previous page
      if (products.length === 1 && currentPage > 1) {
        setCurrentPage(currentPage - 1);
      }
    } catch (error) {
      console.error("Error deleting the session:", error);
      alert(error);
    } finally {
      setIsDeleting(false);
      setIsDel(false);
    }
  };

  const changeProductStatus = async (id, active_product) => {
    if (isTogglingStatus) return;
    setIsTogglingStatus(true);

    const product = products.find((p) => p.id === id);
    if (!product) {
      toast.dismiss();
      toast.error(t("productsList.toast.productNotFound"));
      setIsTogglingStatus(false);
      return;
    }
    if (product.active === active_product) {
      toast.dismiss();
      toast.warning(
        t("productsList.toast.alreadyStatus", {
          name: product.name,
          status: product.active
            ? t("productsList.visibility.visible")
            : t("productsList.visibility.hidden"),
        })
      );
      setIsTogglingStatus(false);
      return;
    }

    toast.dismiss();
    const actionKey = active_product
      ? "productsList.actions.activating"
      : "productsList.actions.deactivating";
    const toastId = toast.loading(
      t("productsList.toast.processing", { action: t(actionKey) })
    );

    try {
      if (!isAuthenticated) {
        localStorage.removeItem("user");
        window.location.href = "/";
        return;
      }
      const response = await fetch(`${baseUrl}/api/v1/attnd-supplier/services/activity`, {
        method: "PUT",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          lang: getLangFromLocalStorage(),

        },
        body: JSON.stringify({
          service_id: id,
          active: active_product,
        }),
      });

      if (!response.ok) {
        if (response.status === 401) {
          navigate("/login");
          throw new Error("Unauthorized access. Please log in again.");
        }
        const errorData = await response.json();
        throw new Error(errorData.error || errorData.message);
      }

      const updatedProducts = products.map((p) => {
        if (p.id === id) {
          toast.update(toastId, {
            render: t("productsList.toast.marked", {
              name: p.name,
              visibility: active_product
                ? t("productsList.visibility.visible")
                : t("productsList.visibility.hidden"),
            }),
            type: "success",
            isLoading: false,
            autoClose: 3000,
            closeOnClick: true,
            pauseOnHover: false,
          });
          return { ...p, active: active_product };
        }
        return p;
      });
      setProducts(updatedProducts);
    } catch (error) {
      toast.update(toastId, {
        render: t("productsList.toast.errorAction", {
          action: t(actionKey),
        }) + `: ${error.message}`,
        type: "error",
        isLoading: false,
        autoClose: 5000,
      });
    } finally {
      setIsTogglingStatus(false);
    }
  };

  // if (loading) {
  //   return (
  //     <div className="loader-container">
  //       <Loader />
  //     </div>
  //   );
  // }
  return (
    <div className="my-14 mx-5">
      <AddProductsHeader title={t("productsList.title")} hasDes={false} />
      <SearchAndAddProductBar setSearchValue={handleSearchChange} searchValue={searchValue} />
    {loading ? (
        <div className="loader-container">
          <Loader />
        </div>
      ) : (
      <CurrentProducts
        currentProducts={products}
        navigate={navigate}
        setIsDel={setIsDel}
        changeProductStatus={changeProductStatus}
      />
      )}

      {totalPages > 1 && (
        <Pagination
          totalPages={totalPages}
          currentPage={currentPage}
          handlePageChange={handlePageChange}
        >
          <RenderPaginationNumbers
            currentPage={currentPage}
            totalPages={totalPages}
            handlePageChange={handlePageChange}
          />
        </Pagination>
      )}

      {isDel && (
        <DeleteModel
          confirmDelete={handleDelete}
          cancelDelete={() => setIsDel(false)}
          isDeleting={isDeleting}
        />
      )}

      <ToastContainer position="top-right" />
    </div>
  );
}

export default ProductsList;
